/*
 *  (c) Copyright 2017 Thalia Bücher GmbH
 *
 *  Created on 06.08.18 13:30 by sertic
 *
 *  This file contains unpublished, proprietary trade secret information of
 *  Thalia Bücher GmbH. Use, transcription, duplication and
 *  modification are strictly prohibited without prior written consent of
 *  Thalia Bücher GmbH.
 */

import registerDatalayerListener from "../../helper/getDatalayerListener";
import { getDataLayer } from "../../helper/getDatalayer";

const datalayer = getDataLayer();

const merkur = {

  cookieConsentCookieName: undefined,
  initialized: false,
  pageViewTracked: false,
  usercentrics: false,
  datalayerHelper: null,

  isDefined: function (value) {
    return typeof value !== 'undefined' && value != null;
  },

  urlParam: function (name) {
    const results = new RegExp("[\\?&]" + name + "=([^&#]*)").exec(window.location.href);
    if (results && merkur.isDefined(results)) {
      return decodeURIComponent(results[1]);
    }
    return undefined;
  },

  getCookie: function (name) {
    const value = "; " + document.cookie;
    const parts = value.split("; " + name + "=");
    if (parts.length === 2) {
      return parts.pop().split(";").shift();
    }
    return undefined;
  },

  trackingEnabled: function () {
    if (navigator.doNotTrack === "yes" || navigator.doNotTrack === "1" ||
      navigator.msDoNotTrack === "1") {
      return false;
    }
    if (merkur.usercentrics) {
      return true;
    }
    const cookieConsentCookieValue = merkur.getCookie(merkur.cookieConsentCookieName);
    return "0" !== cookieConsentCookieValue;
  },

  trackGenericEvent: async (eventType, jsonData) => {
    if (merkur.trackingEnabled() || (("optedin" === eventType) || ("optedout" === eventType))) {

      jsonData.merkurConsentGiven = merkur.datalayerHelper.get("Merkur");

      if (navigator.sendBeacon) {
        const jsonAsStr = JSON.stringify(jsonData);
        const data = new Blob([jsonAsStr], { type: 'application/json' });
        if (!navigator.sendBeacon(`/api/tracking-events/v1/${eventType}`, data)) {
          // Fallback, Browser hat Requests nicht akzeptiert
          const response = await fetch(`/api/tracking-events/v1/${eventType}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            },
            body: jsonAsStr,
            keepalive: true
          });
          return response;
        }
      } else {
        const response = await fetch(`/api/tracking-events/v1/${eventType}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          body: JSON.stringify(jsonData),
          keepalive: true
        });
        return response;
      }
    }
  },

  trackArtikeldetailseite: function (artikelId, matnr) {
    merkur.trackGenericEvent('artikeldetailviewed', {
      artikelId: artikelId,
      matnr: matnr
    });
  },

  trackClickArtikelempfehlung: function (artikelId, matnr, retn) {
    merkur.trackGenericEvent('artikeldetailrecoviewed', {
      artikelId: artikelId,
      matnr: matnr,
      retn: retn,
    });
  },

  trackSearch: function (suchphrase) {
    merkur.trackGenericEvent('usersearched', {
      searchPhrase: suchphrase,
    });
  },

  trackCategoryView: function (categoryName) {
    merkur.trackGenericEvent('categoryviewed', {
      categoryName: categoryName,
    });
  },

  trackAddedToCart: function (artikelIds) {
    merkur.trackGenericEvent('addedtocart', {
      artikelIds: artikelIds,
    });
  },

  isEmpty: function (str) {
    return (!str || 0 === str.length);
  },

  pageViewSeitentypSuche: function (model) {
    // Es wurde eine Suche abgefeuert
    const suchphrase = model.suchbegriff;
    if (merkur.isDefined(suchphrase) && (suchphrase !== "")) {
      merkur.trackSearch(suchphrase);
    }
  },

  pageViewSeitentypKategorieOderBereich: function (model) {
    const kategorieoderbereichname = model.kategorieoderbereichname;
    if (merkur.isDefined(kategorieoderbereichname)) {

      // Übrig sein sollte hier der Kategoriename in der Form "name-nummer".
      merkur.trackCategoryView(kategorieoderbereichname);
    }
  },

  pageViewArtikeldetailseite: function (model) {
    const productId = model.idalt;
    const matnr = model.id;
    if (merkur.isDefined(productId) && merkur.isDefined(matnr)) {
      // Auf die Artikeldetailseite kommen wir auf wzei Arten
      // auf dem normalen Weg und via Prudsys. Der Prudsys-Weg hängt zusätzlich
      // an die URL noch einen retn=... Requestparameter an. Wenn dieser Parameter vorhanden
      // ist, so betrachten wir den Aufruf als Klick auf eine Empfehlung, sonst
      // als normaler Aufruf der Artikeldetailseite
      const retn = merkur.urlParam("retn");
      if (merkur.isDefined(retn)) {
        // Dies wird noch für den externen Einstieg via Newsletter-Empfehlung gebraucht
        merkur.trackClickArtikelempfehlung(productId, matnr, retn);
      } else {
        // Und dies noch für den externen Einstieg via Google Suchergebnis oder
        // aber Klick auf einen Artikel im Shop, siehe dazu Kommentar in productClick()
        merkur.trackArtikeldetailseite(productId, matnr);
      }
    }
  },

  pageViewWarenkorbHinzugefuegt: function () {
    const einzelnerArtikel = merkur.urlParam("artikelId");
    if (merkur.isDefined(einzelnerArtikel)) {
      merkur.trackAddedToCart([einzelnerArtikel]);
      return;
    }
    const mehrereArtikel = merkur.urlParam("artikelIds");
    if (merkur.isDefined(mehrereArtikel)) {
      merkur.trackAddedToCart(mehrereArtikel.split(','));
      return;
    }
  },

  productClick(eventParams) {
    // An dieser Stelle prüfen wir nur, ob der Klick aus einem Empfehlungsslider kommt.
    // Alle anderen Produkt-Klicks werden über das pageView der Artikeldetailseite abgefangen,
    // da wir für die Prudsys-Infrastruktur neben der MatNr auch die alte MyInfo-Nr brauchen.
    const items = eventParams?.ecommerce?.items || [];
    for (const item of items) {
      const productId = item.recooldproductid;
      const matnr = item.item_id;
      const retn = item.recoencoding;
      if (merkur.isDefined(matnr) && merkur.isDefined(retn) && merkur.isDefined(productId)) {
        // Jup, das war ein Klick auf einem Empfehlungsslider
        merkur.trackClickArtikelempfehlung(productId, matnr, retn);
      }
    }
  },

  init: function () {
    // Über diesen Listener hängen wir uns an den Datalayer
    if (datalayer && typeof registerDatalayerListener === "function" && !merkur.initialized) {
      merkur.datalayerHelper = registerDatalayerListener(datalayer, datalayerListenerCallback, true);
      merkur.initialized = true;
    }
  },

  getTrackingData: function () {
    // An dieser Stelle schreiben wir die Kundenklassifizierung
    // In das Pageobject rein. Die Klassifizierung wird via
    // clusterM berechnet, und über Merkur auf alle Seiten gehängt.
    // Das Tracking-Cookie wird via CookieMonster ausgespielt
    return new Promise(resolve => {
      const cookievalue = merkur.getCookie("SBEKUKL");
      if (merkur.isDefined(cookievalue)) {
        const klassifizierung = JSON.parse(atob(cookievalue));
        if (merkur.isDefined(klassifizierung)) {
          const klassifizierungCluster = klassifizierung.cl;
          if (merkur.isDefined(klassifizierungCluster)) {
            const klassifizierungClusterMitPrefixUndSuffix = ',' + klassifizierungCluster + ',';
            resolve({
              kundensegmente: klassifizierungClusterMitPrefixUndSuffix
            });
          } else {
            resolve({
              kundensegmente: ''
            });
          }
        }
      } else {
        resolve({
          kundensegmente: ''
        });
      }
    });
  },

  config: function (usercentrics, cookieConsentCookieName = undefined) {
    merkur.usercentrics = usercentrics;
    merkur.cookieConsentCookieName = cookieConsentCookieName;
    return merkur;
  },

  reset: function () {
    merkur.initialized = false;
    merkur.pageViewTracked = false;
    return merkur;
  }
};

(async () => {
  if (merkur && datalayer) {
    const data = await merkur.getTrackingData();
    const dlPageview = createDlPageview(data);
    document.body.appendChild(dlPageview);
  }
})();

function createDlPageview(data) {
  const dlPageview = document.createElement("dl-pageview");
  for (const [key, value] of Object.entries(data)) {
    dlPageview.setAttribute(key, value);
  }
  return dlPageview;
}

function datalayerListenerCallback(model, message) {
  const { event: eventName, ...eventParams } = message;

  switch (eventName) {
    case "page_view": {
      if (merkur.pageViewTracked) break;
      merkur.pageViewTracked = true;
      // Für page_view: Model und eventParams mergen, damit Seiteneigenschaften verfügbar sind
      handlePageViewEvent({ ...model, ...eventParams });
      break;
    }
    case "select_item":
      // Für select_item: kein Merge mit Model — die angeklickten Items liegen direkt in eventParams
      merkur.productClick(eventParams);
      break;
    default:
      break;
  }
}

function handlePageViewEvent(eventParams) {
  const { seitentyp } = eventParams;
  // Besuch der Warenkorbzwischenseite
  if (document.body.id === "pmCheckout0") {
    // Leider hat die Seite keinen Seitentyp, weshalb wir nur auf die IDs im HTML gehen können
    merkur.pageViewWarenkorbHinzugefuegt();
    return;
  }
  // Wurde eine Suche abgesetzt?
  if (seitentyp === "suche") {
    // Es wurde eine Suche abgefeuert
    merkur.pageViewSeitentypSuche(eventParams);
    return;
  }
  // Besuch einer Kategorieseite?
  if (seitentyp === "kategorie" || seitentyp === "bereich") {
    // Der Kategoriepfad und der Seitentyp wird vom SuchOCS in das Datalayer geschrieben
    merkur.pageViewSeitentypKategorieOderBereich(eventParams);
    return;
  }
  // Besuch einer Artikeldetailseite
  if (seitentyp === "produkt") {
    merkur.pageViewArtikeldetailseite(eventParams);
    return;
  }
}

window.merkur = merkur;

initMerkur();

function initMerkur() {
    if (merkur) {
        merkur.config(true).init();
    } else {
        // console.debug("[Merkur] - Tracking konnte nicht initialisiert werden");
    }
}

export const init = merkur.init;
export const config = merkur.config;
export const reset = merkur.reset;
export const trackGenericEvent = merkur.trackGenericEvent;
export { createDlPageview };
